import { debounce } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { getRepoBranches } from '~/services/organizationService'
import { BetterToast } from '~/utils/toast'

export type UseBranchListProps = {
  conn_type: string
  full_name: string
  /** When true, branches are only fetched when loadIfNeeded() is called (e.g. on dropdown focus) */
  lazy?: boolean
}

export function useBranchList({ conn_type, full_name, lazy = false }: UseBranchListProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const [branches, setBranches] = useState<string[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [cursor, setCursor] = useState('')
  const [search, setSearch] = useState('')
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasLoadedRef = useRef(false)

  const loadBranches = useCallback(
    async (page: number = 0, search: string = '', cursor: string = '') => {
      try {
        if (!currentWorkspace?.uuid) {
          console.error('Workspace UUID not available')
          return []
        }

        setIsLoading(true)

        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }

        const controller = new AbortController()
        abortControllerRef.current = controller

        const result = await getRepoBranches(
          conn_type,
          full_name,
          page,
          currentWorkspace.uuid,
          search,
          cursor,
          {
            signal: controller.signal
          }
        )
        const newBranches = result.branches

        setHasMore(result.has_more !== null && result.has_more)

        if (result.cursor) {
          setCursor(result.cursor)
          setBranches(newBranches)
          return newBranches
        } else {
          let combinedBranches = newBranches
          if (page === 0) {
            setBranches(newBranches)
          } else {
            setBranches(prevBranches => {
              combinedBranches = Array.from(new Set([...prevBranches, ...newBranches]))
              return combinedBranches
            })
          }

          const nextPage = page + 1
          setPage(nextPage)
          return combinedBranches
        }
      } catch (error: unknown) {
        if (error instanceof Error && !error.message.includes('The user aborted a request')) {
          BetterToast.error('Failed to get branches')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [conn_type, full_name, currentWorkspace]
  )

  const debouncedSearchRef = useRef(
    debounce(async (search: string) => {
      const allBranch = await loadBranches(0, search, '')
      setSearch(search)
      setPage(0)
      return allBranch
    }, 800)
  )

  // Update debounced function when loadBranches changes
  useEffect(() => {
    debouncedSearchRef.current = debounce(async (search: string) => {
      const allBranch = await loadBranches(0, search, '')
      setSearch(search)
      setPage(0)
      return allBranch
    }, 800)
  }, [loadBranches])

  // Load initial branches when hook mounts or connection details change (only when not lazy)
  useEffect(() => {
    if (!lazy && conn_type && full_name) {
      loadBranches(0, '', '')
    }
  }, [lazy, conn_type, full_name, loadBranches])

  const loadIfNeeded = useCallback(async () => {
    if (!conn_type || !full_name || hasLoadedRef.current) return
    hasLoadedRef.current = true
    await loadBranches(0, '', '')
  }, [conn_type, full_name, loadBranches])

  // Reset hasLoaded when repo changes so we fetch again for the new repo
  useEffect(() => {
    hasLoadedRef.current = false
  }, [conn_type, full_name])

  const onSearch = useCallback((search: string) => {
    return debouncedSearchRef.current(search)
  }, [])

  const onLoadMore = useCallback(
    async (clearSearch: boolean = false): Promise<string[] | undefined> => {
      let allBranch
      if (clearSearch) {
        setSearch('')
        setPage(0)
        setCursor('')
        allBranch = await loadBranches(0, '', '')
      } else {
        allBranch = await loadBranches(page, search, cursor)
      }
      return allBranch
    },
    [loadBranches, page, search, cursor]
  )

  return {
    branches,
    hasMore,
    isLoading,
    loadIfNeeded,
    onLoadMore,
    onSearch
  }
}
